import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  busList,
  busStudentsWithFees,
  busStudentBulkFeeUpdate,
} from "../../../api/Transport";

import { SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";

/* ============================================================
   HELPERS
============================================================ */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeSearch = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const feeKey = (fee) =>
  `${fee.year}-${fee.month}-${String(fee.type || "")
    .trim()
    .toLowerCase()}`;

const feeOrder = (fee) => num(fee.year) * 12 + num(fee.month);

const extractList = (res) => {
  const d = res?.data?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  return [];
};

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

/* status label shown on every month tile */
const feeStatus = (fee) => {
  if (fee.is_new) return { label: "New", tone: "new" };

  const final = num(fee.final_amount);
  const paid = num(fee.paid_amount);

  if (final <= 0) return { label: "Waived", tone: "waived" };
  if (paid >= final) return { label: "Paid", tone: "paid" };
  if (paid > 0) return { label: "Partial", tone: "partial" };
  return { label: "Due", tone: "due" };
};

/*
 Relation name returned by the bus students API for the fee rows.
 Change here if your Laravel relation is named differently.
*/
const FEES_RELATION = "bus_fee_details";

/* ============================================================
   COMPONENT
============================================================ */

export default function BusFeeUpdate() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");

  const [buses, setBuses] = useState([]);
  const [busId, setBusId] = useState("");

  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // keyed by bus_student id
  const [updateStatus, setUpdateStatus] = useState({});

  // UI only: which student cards are open, and the quick filter chip
  const [expanded, setExpanded] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");

  // snapshot of saved state, used for dirty detection
  const originalRef = useRef({});

  /* ---------- add-months modal ---------- */
  const now = new Date();
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    type: "Bus Fee",
    startMonth: now.getMonth() + 1,
    startYear: now.getFullYear(),
    count: 1,
    source: "stop_fee",
    amount: "",
  });

  /* ==========================================================
     PREPARE / COMPARE
  ========================================================== */

  const prepareStudent = (student) => {
    const fees = (student?.[FEES_RELATION] || [])
      .map((fee) => {
        const actual = num(fee.actual_amount);
        const paid = num(fee.paid_amount);
        const final = Math.max(num(fee.final_amount), paid);

        return {
          id: fee.id || null,
          month: num(fee.month),
          year: num(fee.year),
          type: fee.type || "Bus Fee",
          actual_amount: actual,
          paid_amount: paid,
          final_amount: final,
          discount: Math.max(actual - final, 0),
          due_amount: Math.max(final - paid, 0),
          is_new: false,
        };
      })
      .sort((a, b) => feeOrder(a) - feeOrder(b));

    return {
      ...student,
      fees,
      discount_remark: student.discount_remark || "",
    };
  };

  const createComparable = (student) => ({
    discount_remark: String(student.discount_remark || ""),
    fees: (student.fees || [])
      .map((fee) => ({
        id: fee.id || null,
        key: feeKey(fee),
        final_amount: num(fee.final_amount),
      }))
      .sort((a, b) => a.key.localeCompare(b.key)),
  });

  const isDirty = (student) => {
    const original = originalRef.current[student.id];
    if (!original) return true;
    return (
      JSON.stringify(createComparable(student)) !== JSON.stringify(original)
    );
  };

  // UI only: has this single month tile been changed since last save?
  const isFeeChanged = (student, fee) => {
    if (fee.is_new) return true;
    const original = originalRef.current[student.id];
    if (!original) return true;
    const saved = original.fees.find((f) => f.key === feeKey(fee));
    return !saved || saved.final_amount !== num(fee.final_amount);
  };

  const dirtyStudents = useMemo(
    () => students.filter((s) => isDirty(s)),
    [students],
  );

  /* ==========================================================
     INITIAL LOAD
  ========================================================== */

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("sessions") ?? "[]");
    setSessions(stored);

    const active = stored.find((s) => String(s.status) === "1") || stored[0];
    if (active?.id) setSessionId(String(active.id));

    loadBuses();
  }, []);

  const loadBuses = async () => {
    setLoading(true);
    try {
      const res = await busList("limit=all");
      const list = extractList(res).filter((b) => String(b.status) !== "3");
      setBuses(list);
    } catch (error) {
      setBuses([]);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load buses",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     FETCH STUDENTS
  ========================================================== */

  const fetchStudents = async (bid, sid) => {
    if (!bid || !sid) return;

    setLoading(true);
    try {
      const res = await busStudentsWithFees(`bus_id=${bid}&session_id=${sid}`);

      if (!res?.data?.status) {
        setStudents([]);
        originalRef.current = {};
        return SweetAlertShow(
          "info",
          "No Students",
          res?.data?.message || "No students found for this bus.",
        );
      }

      const prepared = extractList(res).map(prepareStudent);

      setStudents(prepared);
      setSearch("");
      setStatusFilter("all");
      setUpdateStatus({});

      // small buses open fully, bigger ones start collapsed
      setExpanded(
        prepared.length <= 5
          ? Object.fromEntries(prepared.map((s) => [s.id, true]))
          : {},
      );

      const snapshot = {};
      prepared.forEach((s) => {
        snapshot[s.id] = createComparable(s);
      });
      originalRef.current = snapshot;

      if (!prepared.length) {
        SweetAlertShow(
          "info",
          "No Students",
          "No students assigned to this bus.",
        );
      }
    } catch (error) {
      setStudents([]);
      originalRef.current = {};
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load students",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = (value) => {
    setSessionId(value);
    setStudents([]);
    setUpdateStatus({});
    setExpanded({});
    originalRef.current = {};
    if (busId && value) fetchStudents(busId, value);
  };

  const handleBusChange = (value) => {
    setBusId(value);
    setStudents([]);
    setUpdateStatus({});
    setExpanded({});
    originalRef.current = {};
    if (value && sessionId) fetchStudents(value, sessionId);
  };

  /* ==========================================================
     FEE COLUMNS (one per month/year/type)
  ========================================================== */

  const feeColumns = useMemo(() => {
    const map = new Map();
    students.forEach((s) => {
      (s.fees || []).forEach((fee) => {
        const key = feeKey(fee);
        if (!map.has(key)) {
          map.set(key, {
            key,
            month: fee.month,
            year: fee.year,
            type: fee.type,
          });
        }
      });
    });
    return Array.from(map.values()).sort(
      (a, b) => feeOrder(a) - feeOrder(b) || a.type.localeCompare(b.type),
    );
  }, [students]);

  /* ==========================================================
     TOTALS
  ========================================================== */

  const calculateTotals = (student) =>
    (student.fees || []).reduce(
      (t, fee) => ({
        actual: t.actual + num(fee.actual_amount),
        paid: t.paid + num(fee.paid_amount),
        discount: t.discount + num(fee.discount),
        payable: t.payable + num(fee.final_amount),
        due: t.due + num(fee.due_amount),
      }),
      { actual: 0, paid: 0, discount: 0, payable: 0, due: 0 },
    );

  const busTotals = useMemo(
    () =>
      students.reduce(
        (t, s) => {
          const x = calculateTotals(s);
          return {
            actual: t.actual + x.actual,
            paid: t.paid + x.paid,
            discount: t.discount + x.discount,
            payable: t.payable + x.payable,
            due: t.due + x.due,
          };
        },
        { actual: 0, paid: 0, discount: 0, payable: 0, due: 0 },
      ),
    [students],
  );

  /* ==========================================================
     FILTER (search + quick chips)
  ========================================================== */

  const chipCounts = useMemo(() => {
    const counts = { all: students.length, due: 0, paid: 0, unsaved: 0 };
    students.forEach((s) => {
      const t = calculateTotals(s);
      if (t.due > 0) counts.due++;
      else if (t.payable > 0) counts.paid++;
      if (isDirty(s)) counts.unsaved++;
    });
    return counts;
  }, [students]);

  const visibleStudents = useMemo(() => {
    const q = normalizeSearch(search);

    return students.filter((s) => {
      if (q) {
        const name = normalizeSearch(s?.student?.name);
        const phone = normalizeSearch(s?.student?.phone);
        const reg = normalizeSearch(s?.student?.registration_no);
        if (!(name.includes(q) || phone.includes(q) || reg.includes(q))) {
          return false;
        }
      }

      if (statusFilter === "all") return true;

      const t = calculateTotals(s);
      if (statusFilter === "due") return t.due > 0;
      if (statusFilter === "paid") return t.due <= 0 && t.payable > 0;
      if (statusFilter === "unsaved") return isDirty(s);
      return true;
    });
  }, [students, search, statusFilter]);

  /* ==========================================================
     EDIT HANDLERS
  ========================================================== */

  const toggleCard = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () =>
    setExpanded(Object.fromEntries(visibleStudents.map((s) => [s.id, true])));

  const collapseAll = () => setExpanded({});

  const patchFee = (studentId, key, patcher) => {
    setStudents((prev) =>
      prev.map((student) => {
        if (student.id !== studentId) return student;

        return {
          ...student,
          fees: student.fees.map((fee) =>
            feeKey(fee) === key ? patcher(fee) : fee,
          ),
        };
      }),
    );
  };

  const recalc = (fee, finalAmount) => {
    const actual = num(fee.actual_amount);
    const paid = num(fee.paid_amount);
    return {
      ...fee,
      final_amount: finalAmount,
      discount: Math.max(actual - finalAmount, 0),
      due_amount: Math.max(finalAmount - paid, 0),
    };
  };

  // while typing: only cap at actual (min-paid is enforced on blur)
  const handleFeeChange = (studentId, key, value) => {
    patchFee(studentId, key, (fee) => {
      const actual = num(fee.actual_amount);
      const typed = Math.min(
        num(String(value).replace(/[^0-9.]/g, "")),
        actual,
      );
      return recalc(fee, typed);
    });
  };

  const handleFeeBlur = (studentId, key) => {
    patchFee(studentId, key, (fee) => {
      const paid = num(fee.paid_amount);
      return num(fee.final_amount) < paid ? recalc(fee, paid) : fee;
    });
  };

  const handleRemarkChange = (studentId, value) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId ? { ...s, discount_remark: value } : s,
      ),
    );
  };

  /* ==========================================================
     ADD MONTHS (new instalments, saved with Update)
  ========================================================== */

  const buildPeriods = (form) => {
    const count = Math.max(1, Math.min(24, num(form.count)));
    const startMonth = num(form.startMonth);
    const startYear = num(form.startYear);
    const periods = [];

    if (!startMonth || !startYear) return periods;

    for (let i = 0; i < count; i++) {
      const idx = startMonth - 1 + i;
      periods.push({
        month: (idx % 12) + 1,
        year: startYear + Math.floor(idx / 12),
      });
    }
    return periods;
  };

  // live preview shown inside the modal
  const addPreview = useMemo(() => {
    const type = String(addForm.type || "").trim();
    const periods = buildPeriods(addForm);

    let entries = 0;
    let skipped = 0;
    const touched = new Set();

    if (type) {
      students.forEach((student) => {
        const actual =
          addForm.source === "custom"
            ? num(addForm.amount)
            : num(student.bus_stop_fee);

        periods.forEach((p) => {
          const exists = student.fees.some(
            (f) => feeKey(f) === feeKey({ ...p, type }),
          );
          if (exists) return;

          if (actual <= 0) {
            skipped++;
            return;
          }

          entries++;
          touched.add(student.id);
        });
      });
    }

    return { periods, entries, skipped, studentCount: touched.size };
  }, [addForm, students]);

  const applyAddMonths = () => {
    const type = String(addForm.type || "").trim();
    const startMonth = num(addForm.startMonth);
    const startYear = num(addForm.startYear);

    if (!type) {
      return SweetAlertShow("warning", "Fee Type", "Please enter a fee type.");
    }
    if (!startMonth || !startYear) {
      return SweetAlertShow(
        "warning",
        "Start Month",
        "Please select start month and year.",
      );
    }
    if (addForm.source === "custom" && num(addForm.amount) <= 0) {
      return SweetAlertShow(
        "warning",
        "Amount",
        "Please enter an amount greater than 0.",
      );
    }

    const periods = buildPeriods(addForm);

    let added = 0;
    let skippedNoAmount = 0;
    const touched = new Set();

    setStudents((prev) =>
      prev.map((student) => {
        const actual =
          addForm.source === "custom"
            ? num(addForm.amount)
            : num(student.bus_stop_fee);

        const newFees = [];

        periods.forEach((p) => {
          const candidate = { ...p, type };
          const exists = student.fees.some(
            (f) => feeKey(f) === feeKey(candidate),
          );
          if (exists) return;

          if (actual <= 0) {
            skippedNoAmount++;
            return;
          }

          newFees.push({
            id: null,
            month: p.month,
            year: p.year,
            type,
            actual_amount: actual,
            paid_amount: 0,
            final_amount: actual,
            discount: 0,
            due_amount: actual,
            is_new: true,
          });
        });

        if (!newFees.length) return student;

        added += newFees.length;
        touched.add(student.id);

        return {
          ...student,
          fees: [...student.fees, ...newFees].sort(
            (a, b) => feeOrder(a) - feeOrder(b),
          ),
        };
      }),
    );

    setShowAddModal(false);

    SweetAlertShow(
      added > 0 ? "success" : "info",
      "Fees Prepared",
      `${added} fee entries added for ${touched.size} students.` +
        (skippedNoAmount
          ? ` ${skippedNoAmount} skipped because the amount was 0.`
          : "") +
        (added ? " Review them and click Update to save." : ""),
    );
  };

  /* ==========================================================
     VALIDATE / PAYLOAD / SAVE
  ========================================================== */

  const validateStudent = (student) => {
    const errors = [];

    student.fees.forEach((fee) => {
      const label = `${MONTHS[fee.month - 1] || fee.month} ${fee.year}`;
      const actual = num(fee.actual_amount);
      const paid = num(fee.paid_amount);
      const final = num(fee.final_amount);

      if (final < paid) {
        errors.push(
          `${label}: payable ₹${money(final)} is less than paid ₹${money(paid)}.`,
        );
      }
      if (final > actual) {
        errors.push(`${label}: payable cannot exceed actual fee.`);
      }
    });

    return errors;
  };

  const buildPayload = (student) => ({
    bus_id: student.bus_id,
    bus_student_id: student.id,
    discount_remark: student.discount_remark || "",
    fees: student.fees.map((fee) => ({
      id: fee.id || null,
      month: fee.month,
      year: fee.year,
      type: fee.type,
      actual_amount: num(fee.actual_amount),
      final_amount: num(fee.final_amount),
    })),
  });

  // merge server response back into state and mark as saved
  const applyServerResult = (student, data) => {
    if (!data?.bus_student) return false;

    const merged = prepareStudent({
      ...student,
      ...data.bus_student,
      [FEES_RELATION]: data.fees || [],
    });

    setStudents((prev) => prev.map((s) => (s.id === student.id ? merged : s)));
    originalRef.current[merged.id] = createComparable(merged);
    return true;
  };

  const saveStudent = async (student) => {
    try {
      const res = await busStudentBulkFeeUpdate(buildPayload(student));

      if (!res?.data?.status) {
        return { ok: false, message: res?.data?.message || "Update failed" };
      }

      if (!applyServerResult(student, res.data.data)) {
        await fetchStudents(busId, sessionId);
      }

      return { ok: true, message: res.data.message || "Updated" };
    } catch (error) {
      const raw =
        error?.response?.data?.message || error?.message || "Update failed";
      return {
        ok: false,
        message: Array.isArray(raw) ? raw.join(", ") : String(raw),
      };
    }
  };

  const updateOne = async (student) => {
    if (!isDirty(student)) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "Nothing to save for this student.",
      );
    }

    const errors = validateStudent(student);
    if (errors.length) {
      return SweetAlertShow("error", "Validation Error", errors.join("\n"));
    }

    setUpdating(true);
    const result = await saveStudent(student);
    setUpdating(false);

    setUpdateStatus((prev) => ({
      ...prev,
      [student.id]: {
        status: result.ok ? "success" : "error",
        message: result.message,
      },
    }));

    SweetAlertShow(
      result.ok ? "success" : "error",
      result.ok ? "Updated" : "Update Failed",
      result.ok
        ? `${student.student?.name || "Student"} updated successfully.`
        : result.message,
    );
  };

  const updateAll = async () => {
    const changed = students.filter((s) => isDirty(s));

    if (!changed.length) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "No student has unsaved changes.",
      );
    }

    for (const student of changed) {
      const errors = validateStudent(student);
      if (errors.length) {
        // open the card so the problem is visible
        setExpanded((prev) => ({ ...prev, [student.id]: true }));

        return SweetAlertShow(
          "error",
          "Validation Error",
          `${student.student?.name || "Student"}: ${errors.join(", ")}`,
        );
      }
    }

    setUpdating(true);

    const status = {};
    const chunkSize = 10;

    try {
      for (let i = 0; i < changed.length; i += chunkSize) {
        const chunk = changed.slice(i, i + chunkSize);

        await Promise.all(
          chunk.map(async (student) => {
            const result = await saveStudent(student);
            status[student.id] = {
              status: result.ok ? "success" : "error",
              message: result.message,
            };
          }),
        );

        setUpdateStatus({ ...status });
      }

      const success = Object.values(status).filter(
        (x) => x.status === "success",
      ).length;
      const failed = changed.length - success;

      SweetAlertShow(
        failed === 0 ? "success" : "warning",
        "Update Completed",
        `Changed: ${changed.length}, Updated: ${success}, Failed: ${failed}`,
      );
    } finally {
      setUpdating(false);
    }
  };

  /* ==========================================================
     RENDER
  ========================================================== */

  const selectedBus = buses.find((b) => String(b.id) === String(busId));

  const currentStep =
    !busId || !sessionId ? 1 : dirtyStudents.length > 0 ? 3 : 2;

  const collectionPct =
    busTotals.payable > 0
      ? Math.min(100, Math.round((busTotals.paid / busTotals.payable) * 100))
      : 0;

  const statCards = [
    {
      label: "Actual Fees",
      value: busTotals.actual,
      icon: "ri-money-rupee-circle-line",
      tone: "neutral",
    },
    {
      label: "Discount",
      value: busTotals.discount,
      icon: "ri-price-tag-3-line",
      tone: "warn",
    },
    {
      label: "Payable",
      value: busTotals.payable,
      icon: "ri-file-list-3-line",
      tone: "primary",
    },
    {
      label: "Collected",
      value: busTotals.paid,
      icon: "ri-checkbox-circle-line",
      tone: "ok",
    },
    {
      label: "Pending Due",
      value: busTotals.due,
      icon: "ri-error-warning-line",
      tone: "danger",
    },
  ];

  const chips = [
    { key: "all", label: "All", icon: "ri-group-line" },
    { key: "due", label: "Has Due", icon: "ri-error-warning-line" },
    { key: "paid", label: "Fully Paid", icon: "ri-checkbox-circle-line" },
    { key: "unsaved", label: "Unsaved", icon: "ri-edit-2-line" },
  ];

  return (
    <>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ background: "rgba(0,0,0,.25)", zIndex: 9999 }}
        >
          <div className="spinner-border text-primary" />
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12 bfu-root">
                    {/* ================= HEADER + FILTERS ================= */}
                    <div className="bfu-panel mb-3">
                      <div className="bfu-title-row">
                        <div className="bfu-title">
                          <span className="bfu-title-icon">
                            <i className="ri-bus-2-line" />
                          </span>
                          <div>
                            <h5>Bus Fee Update</h5>
                            <small>
                              Adjust monthly transport fees for all students of
                              a bus
                            </small>
                          </div>
                        </div>

                        <div className="bfu-steps">
                          {[
                            "Choose session & bus",
                            "Review & edit fees",
                            "Save changes",
                          ].map((label, i) => {
                            const n = i + 1;
                            const state =
                              n < currentStep
                                ? "done"
                                : n === currentStep
                                  ? "active"
                                  : "";
                            return (
                              <div key={label} className={`bfu-step ${state}`}>
                                <span className="bfu-step-dot">
                                  {state === "done" ? (
                                    <i className="ri-check-line" />
                                  ) : (
                                    n
                                  )}
                                </span>
                                <span className="bfu-step-label">{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="row g-3 mt-0">
                        <div className="col-lg-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select"
                              value={sessionId}
                              onChange={(e) =>
                                handleSessionChange(e.target.value)
                              }
                            >
                              <option value="">Select Session</option>
                              {sessions.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.session}
                                  {String(s.status) === "1" ? " (Active)" : ""}
                                </option>
                              ))}
                            </select>
                            <label>Session</label>
                          </div>
                        </div>

                        <div className="col-lg-4 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select"
                              value={busId}
                              onChange={(e) => handleBusChange(e.target.value)}
                              disabled={!sessionId}
                            >
                              <option value="">Select Bus</option>
                              {buses.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.bus_number}
                                  {b.driver_name ? ` (${b.driver_name})` : ""}
                                </option>
                              ))}
                            </select>
                            <label>Bus</label>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-8">
                          <div className="form-floating position-relative">
                            <input
                              type="text"
                              className="form-control pe-5"
                              placeholder="Search student"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              disabled={!students.length}
                              autoComplete="off"
                            />
                            <label>Search student</label>

                            {search ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-link position-absolute p-0 bfu-input-icon"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                              >
                                <i className="ri-close-circle-fill fs-5 text-secondary" />
                              </button>
                            ) : (
                              <i className="ri-search-line position-absolute bfu-input-icon text-muted" />
                            )}
                          </div>
                        </div>

                        <div className="col-lg-2 col-md-4 d-flex">
                          <button
                            className="btn btn-success w-100"
                            onClick={updateAll}
                            disabled={updating || !dirtyStudents.length}
                          >
                            {updating ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="ri-save-3-line me-1" />
                                Update All
                                {dirtyStudents.length > 0 &&
                                  ` (${dirtyStudents.length})`}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ================= EMPTY STATE ================= */}
                    {!students.length && (
                      <div className="bfu-panel bfu-empty">
                        <span className="bfu-empty-icon">
                          <i className="ri-bus-2-line" />
                        </span>
                        <h5>
                          {busId
                            ? "No students on this bus"
                            : "Select a session and a bus"}
                        </h5>
                        <p>
                          {busId
                            ? "No students are assigned to this bus for the selected session."
                            : "The students of the bus and their monthly fees will appear here."}
                        </p>
                      </div>
                    )}

                    {students.length > 0 && (
                      <>
                        {/* ================= BUS STRIP ================= */}
                        {selectedBus && (
                          <div className="bfu-busstrip">
                            <span className="bfu-busstrip-icon">
                              <i className="ri-bus-2-fill" />
                            </span>
                            <div className="bfu-busstrip-main">
                              <strong>{selectedBus.bus_number}</strong>
                              <small>
                                {selectedBus.driver_name || "No driver"}
                                {selectedBus.driver_number
                                  ? ` • ${selectedBus.driver_number}`
                                  : ""}
                              </small>
                            </div>
                            <div className="bfu-busstrip-meta">
                              <span>
                                <i className="ri-group-line" />
                                {students.length} students
                              </span>
                              {selectedBus.capacity ? (
                                <span>
                                  <i className="ri-armchair-line" />
                                  Capacity {selectedBus.capacity}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}

                        {/* ================= SUMMARY ================= */}
                        <div className="bfu-stats">
                          {statCards.map((card) => (
                            <div
                              key={card.label}
                              className={`bfu-stat bfu-tone-${card.tone}`}
                            >
                              <span className="bfu-stat-icon">
                                <i className={card.icon} />
                              </span>
                              <div>
                                <small>{card.label}</small>
                                <strong>₹{money(card.value)}</strong>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="bfu-collect">
                          <div className="bfu-collect-top">
                            <span>Fee collection</span>
                            <strong>{collectionPct}% collected</strong>
                          </div>
                          <div className="bfu-bar">
                            <span style={{ width: `${collectionPct}%` }} />
                          </div>
                        </div>

                        {/* ================= TOOLBAR ================= */}
                        <div className="bfu-toolbar">
                          <div className="bfu-chips">
                            {chips.map((chip) => (
                              <button
                                key={chip.key}
                                type="button"
                                className={`bfu-chipbtn ${
                                  statusFilter === chip.key ? "active" : ""
                                }`}
                                onClick={() => setStatusFilter(chip.key)}
                              >
                                <i className={chip.icon} />
                                {chip.label}
                                <span className="bfu-chipcount">
                                  {chipCounts[chip.key]}
                                </span>
                              </button>
                            ))}
                          </div>

                          <div className="bfu-toolbar-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={expandAll}
                            >
                              <i className="ri-expand-up-down-line me-1" />
                              Expand all
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={collapseAll}
                            >
                              <i className="ri-contract-up-down-line me-1" />
                              Collapse all
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-warning"
                              onClick={() => setShowAddModal(true)}
                              disabled={updating}
                            >
                              <i className="ri-calendar-add-line me-1" />
                              Add Month Fees
                            </button>
                          </div>
                        </div>

                        {/* ================= NO FEES ================= */}
                        {!feeColumns.length ? (
                          <div className="bfu-panel bfu-empty">
                            <span className="bfu-empty-icon">
                              <i className="ri-calendar-line" />
                            </span>
                            <h5>No monthly fees created yet</h5>
                            <p>
                              These students have no fee entries. Use Add Month
                              Fees to create them.
                            </p>
                            <button
                              className="btn btn-warning"
                              onClick={() => setShowAddModal(true)}
                            >
                              <i className="ri-calendar-add-line me-1" />
                              Add Month Fees
                            </button>
                          </div>
                        ) : visibleStudents.length === 0 ? (
                          <div className="bfu-panel bfu-empty">
                            <span className="bfu-empty-icon">
                              <i className="ri-user-search-line" />
                            </span>
                            <h5>No student matches</h5>
                            <p>Try a different search or filter.</p>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setSearch("");
                                setStatusFilter("all");
                              }}
                            >
                              Clear search & filters
                            </button>
                          </div>
                        ) : (
                          /* ================= STUDENT CARDS ================= */
                          <div className="bfu-list">
                            {visibleStudents.map((student, rowIndex) => {
                              const totals = calculateTotals(student);
                              const dirty = isDirty(student);
                              const status = updateStatus[student.id];
                              const open = !!expanded[student.id];
                              const pct =
                                totals.payable > 0
                                  ? Math.min(
                                      100,
                                      Math.round(
                                        (totals.paid / totals.payable) * 100,
                                      ),
                                    )
                                  : 0;

                              return (
                                <div
                                  key={student.id}
                                  className={`bfu-card ${dirty ? "is-dirty" : ""} ${
                                    open ? "is-open" : ""
                                  }`}
                                >
                                  {/* ---------- card header ---------- */}
                                  <div
                                    className="bfu-card-head"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={open}
                                    onClick={() => toggleCard(student.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleCard(student.id);
                                      }
                                    }}
                                  >
                                    <i className="ri-arrow-right-s-line bfu-chev" />

                                    <div
                                      className={`bfu-avatar bfu-av-${
                                        num(student.id) % 6
                                      }`}
                                    >
                                      {initials(student.student?.name)}
                                    </div>

                                    <div className="bfu-who">
                                      <strong>
                                        <span className="bfu-sn">
                                          {rowIndex + 1}.
                                        </span>{" "}
                                        {student.student?.name || "-"}
                                      </strong>
                                      <small>
                                        Reg{" "}
                                        {student.student?.registration_no ||
                                          "-"}
                                        {" • "}
                                        {student.student?.phone || "No mobile"}
                                      </small>
                                    </div>

                                    <div className="bfu-stop">
                                      <span>
                                        <i className="ri-map-pin-2-line" />
                                        {student.stop?.stop_name ||
                                          student.stop?.name ||
                                          student.stop?.title ||
                                          "No stop"}
                                      </span>
                                      <small>
                                        ₹{money(student.bus_stop_fee)}/month
                                        {student.payment_type
                                          ? ` • ${student.payment_type}`
                                          : ""}
                                      </small>
                                    </div>

                                    <div className="bfu-mini">
                                      <div>
                                        <small>Payable</small>
                                        <strong>
                                          ₹{money(totals.payable)}
                                        </strong>
                                      </div>
                                      <div>
                                        <small>Paid</small>
                                        <strong className="t-ok">
                                          ₹{money(totals.paid)}
                                        </strong>
                                      </div>
                                      <div>
                                        <small>Due</small>
                                        {totals.due > 0 ? (
                                          <strong className="t-danger">
                                            ₹{money(totals.due)}
                                          </strong>
                                        ) : (
                                          <strong className="t-ok">
                                            <i className="ri-check-line" />
                                            Cleared
                                          </strong>
                                        )}
                                      </div>
                                    </div>

                                    <div className="bfu-head-actions">
                                      {dirty && (
                                        <span className="bfu-unsaved">
                                          <i className="ri-edit-2-line" />
                                          Unsaved
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${
                                          dirty
                                            ? "btn-primary"
                                            : "btn-outline-secondary"
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateOne(student);
                                        }}
                                        disabled={updating || !dirty}
                                      >
                                        <i
                                          className={`${
                                            dirty
                                              ? "ri-save-line"
                                              : "ri-check-double-line"
                                          } me-1`}
                                        />
                                        {dirty ? "Update" : "Saved"}
                                      </button>
                                    </div>
                                  </div>

                                  <div className="bfu-bar bfu-bar-thin">
                                    <span style={{ width: `${pct}%` }} />
                                  </div>

                                  {status && (
                                    <div
                                      className={`bfu-msg ${
                                        status.status === "success"
                                          ? "ok"
                                          : "err"
                                      }`}
                                    >
                                      <i
                                        className={
                                          status.status === "success"
                                            ? "ri-checkbox-circle-fill"
                                            : "ri-error-warning-fill"
                                        }
                                      />
                                      {status.status === "success"
                                        ? "Saved successfully"
                                        : status.message}
                                    </div>
                                  )}

                                  {/* ---------- card body ---------- */}
                                  {open && (
                                    <div className="bfu-body">
                                      <div className="bfu-tiles">
                                        {feeColumns.map((col) => {
                                          const fee = student.fees.find(
                                            (f) => feeKey(f) === col.key,
                                          );

                                          if (!fee) {
                                            return (
                                              <div
                                                key={`${student.id}-${col.key}`}
                                                className="bfu-tile bfu-tile-empty"
                                              >
                                                <strong>
                                                  {MONTHS[col.month - 1] ||
                                                    col.month}{" "}
                                                  {col.year}
                                                </strong>
                                                <small>Not applied</small>
                                              </div>
                                            );
                                          }

                                          const paid = num(fee.paid_amount);
                                          const st = feeStatus(fee);
                                          const changed = isFeeChanged(
                                            student,
                                            fee,
                                          );

                                          return (
                                            <div
                                              key={`${student.id}-${col.key}`}
                                              className={`bfu-tile ${
                                                changed ? "is-changed" : ""
                                              }`}
                                            >
                                              <div className="bfu-tile-top">
                                                <div>
                                                  <strong>
                                                    {MONTHS[fee.month - 1] ||
                                                      fee.month}{" "}
                                                    {fee.year}
                                                  </strong>
                                                  <small>{fee.type}</small>
                                                </div>
                                                <span
                                                  className={`bfu-chip bfu-chip-${st.tone}`}
                                                >
                                                  {st.label}
                                                </span>
                                              </div>

                                              <label className="bfu-label">
                                                Payable amount
                                              </label>
                                              <div className="input-group input-group-sm">
                                                <span className="input-group-text">
                                                  ₹
                                                </span>
                                                <input
                                                  type="number"
                                                  min={paid}
                                                  max={num(fee.actual_amount)}
                                                  step="0.01"
                                                  className="form-control fw-semibold"
                                                  value={fee.final_amount}
                                                  onChange={(e) =>
                                                    handleFeeChange(
                                                      student.id,
                                                      col.key,
                                                      e.target.value,
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    handleFeeBlur(
                                                      student.id,
                                                      col.key,
                                                    )
                                                  }
                                                />
                                              </div>

                                              <div className="bfu-tile-meta">
                                                <span>
                                                  Actual{" "}
                                                  {fee.discount > 0 ? (
                                                    <s>
                                                      ₹
                                                      {money(fee.actual_amount)}
                                                    </s>
                                                  ) : (
                                                    <b>
                                                      ₹
                                                      {money(fee.actual_amount)}
                                                    </b>
                                                  )}
                                                </span>
                                                {fee.discount > 0 && (
                                                  <span className="bfu-off">
                                                    −₹{money(fee.discount)} off
                                                  </span>
                                                )}
                                              </div>

                                              <div className="bfu-tile-meta">
                                                <span className="t-ok">
                                                  Paid <b>₹{money(paid)}</b>
                                                </span>
                                                <span
                                                  className={
                                                    fee.due_amount > 0
                                                      ? "t-danger"
                                                      : "t-muted"
                                                  }
                                                >
                                                  Due{" "}
                                                  <b>
                                                    ₹{money(fee.due_amount)}
                                                  </b>
                                                </span>
                                              </div>

                                              {paid > 0 && (
                                                <div className="bfu-hint">
                                                  <i className="ri-lock-line" />
                                                  Can't go below ₹{money(
                                                    paid,
                                                  )}{" "}
                                                  (already paid)
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="bfu-foot">
                                        <div className="bfu-remark">
                                          <label className="bfu-label">
                                            Discount remark (optional)
                                          </label>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="e.g. Sibling discount, staff ward..."
                                            value={student.discount_remark}
                                            onChange={(e) =>
                                              handleRemarkChange(
                                                student.id,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        </div>

                                        <div className="bfu-totals">
                                          <div>
                                            <small>Actual</small>
                                            <strong>
                                              ₹{money(totals.actual)}
                                            </strong>
                                          </div>
                                          <div>
                                            <small>Discount</small>
                                            <strong className="t-warn">
                                              ₹{money(totals.discount)}
                                            </strong>
                                          </div>
                                          <div>
                                            <small>Payable</small>
                                            <strong>
                                              ₹{money(totals.payable)}
                                            </strong>
                                          </div>
                                          <div>
                                            <small>Paid</small>
                                            <strong className="t-ok">
                                              ₹{money(totals.paid)}
                                            </strong>
                                          </div>
                                          <div>
                                            <small>Due</small>
                                            <strong className="t-danger">
                                              ₹{money(totals.due)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* space so the floating bar never hides the last card */}
                        {dirtyStudents.length > 0 && (
                          <div style={{ height: 80 }} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= FLOATING SAVE BAR ================= */}
      {dirtyStudents.length > 0 && (
        <div className="bfu-root">
          <div className="bfu-floatbar">
            <span>
              <i className="ri-edit-2-line" />
              <strong>{dirtyStudents.length}</strong> student
              {dirtyStudents.length > 1 ? "s" : ""} with unsaved changes
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => setStatusFilter("unsaved")}
              >
                Show them
              </button>
              <button
                type="button"
                className="btn btn-sm btn-success"
                onClick={updateAll}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-3-line me-1" />
                    Update All
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD MONTHS MODAL ================= */}
      {showAddModal && (
        <div className="bfu-root">
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: "rgba(0,0,0,.55)", zIndex: 10050 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="bfu-title">
                    <span className="bfu-title-icon">
                      <i className="ri-calendar-add-line" />
                    </span>
                    <div>
                      <h5 className="modal-title mb-0">Add Month Fees</h5>
                      <small className="text-muted">
                        For every student of this bus. Months a student already
                        has are skipped.
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Fee Type</label>
                      <input
                        type="text"
                        className="form-control"
                        value={addForm.type}
                        onChange={(e) =>
                          setAddForm({ ...addForm, type: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Start Month</label>
                      <select
                        className="form-select"
                        value={addForm.startMonth}
                        onChange={(e) =>
                          setAddForm({
                            ...addForm,
                            startMonth: Number(e.target.value),
                          })
                        }
                      >
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Year</label>
                      <input
                        type="number"
                        className="form-control"
                        value={addForm.startYear}
                        onChange={(e) =>
                          setAddForm({
                            ...addForm,
                            startYear: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">No. of Months</label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        className="form-control"
                        value={addForm.count}
                        onChange={(e) =>
                          setAddForm({ ...addForm, count: e.target.value })
                        }
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label">Amount per month</label>
                      <select
                        className="form-select"
                        value={addForm.source}
                        onChange={(e) =>
                          setAddForm({ ...addForm, source: e.target.value })
                        }
                      >
                        <option value="stop_fee">
                          Use each student's stop fee
                        </option>
                        <option value="custom">
                          Same custom amount for all
                        </option>
                      </select>
                    </div>

                    {addForm.source === "custom" && (
                      <div className="col-12">
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            placeholder="Amount per month"
                            value={addForm.amount}
                            onChange={(e) =>
                              setAddForm({ ...addForm, amount: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}

                    {/* ---------- live preview ---------- */}
                    <div className="col-12">
                      <div className="bfu-preview">
                        <div className="bfu-preview-title">
                          <i className="ri-eye-line" /> Preview
                        </div>

                        <div className="bfu-preview-months">
                          {addPreview.periods.map((p) => (
                            <span key={`${p.year}-${p.month}`}>
                              {MONTHS[p.month - 1]} {p.year}
                            </span>
                          ))}
                        </div>

                        <div className="bfu-preview-text">
                          {addPreview.entries > 0 ? (
                            <>
                              <strong>{addPreview.entries}</strong> fee entries
                              will be added for{" "}
                              <strong>{addPreview.studentCount}</strong> student
                              {addPreview.studentCount !== 1 ? "s" : ""}.
                            </>
                          ) : (
                            <>Nothing new to add with these settings.</>
                          )}
                          {addPreview.skipped > 0 && (
                            <span className="t-warn">
                              {" "}
                              {addPreview.skipped} skipped (amount is 0).
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={applyAddMonths}
                    disabled={addPreview.entries === 0}
                  >
                    <i className="ri-check-double-line me-1" />
                    Add to Table
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= STYLES ================= */}
      <style>{`
        .bfu-root{--bfu-p:var(--bs-primary,#696cff);--bfu-bg:var(--bs-card-bg,#fff);--bfu-tx:var(--bs-body-color,#384154);--bfu-bd:var(--bs-border-color,#e6e9f1);--bfu-mu:var(--bs-secondary-color,#79849a);--bfu-soft:rgba(105,108,255,.08);--bfu-ok:#2e9e4f;--bfu-warn:#d98a00;--bfu-danger:#e5484d;--bfu-info:#3b82f6;--bfu-ok-bg:rgba(46,158,79,.12);--bfu-warn-bg:rgba(217,138,0,.14);--bfu-danger-bg:rgba(229,72,77,.12);--bfu-info-bg:rgba(59,130,246,.12);--bfu-sub:rgba(120,130,160,.07)}
        [data-bs-theme="dark"] .bfu-root{--bfu-bg:#2b2c40;--bfu-tx:#e6e7f0;--bfu-mu:#aaaec2;--bfu-bd:#474961;--bfu-sub:rgba(255,255,255,.04)}
        .bfu-root small{color:var(--bfu-mu)}
        .bfu-panel{background:var(--bfu-bg);border:1px solid var(--bfu-bd);border-radius:14px;padding:18px;box-shadow:0 3px 14px rgba(28,39,69,.04)}
        .t-ok{color:var(--bfu-ok)!important}.t-danger{color:var(--bfu-danger)!important}.t-warn{color:var(--bfu-warn)!important}.t-muted{color:var(--bfu-mu)!important}

        .bfu-title-row{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:16px}
        .bfu-title{display:flex;align-items:center;gap:12px}
        .bfu-title h5{margin:0;font-size:17px;font-weight:700;color:var(--bfu-tx)}
        .bfu-title small{font-size:12px}
        .bfu-title-icon{width:44px;height:44px;border-radius:12px;display:grid;place-items:center;font-size:23px;color:#fff;background:linear-gradient(135deg,#6d7df0,#5365d2);flex-shrink:0}

        .bfu-steps{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
        .bfu-step{display:flex;align-items:center;gap:7px;padding:6px 11px;border-radius:30px;background:var(--bfu-sub);color:var(--bfu-mu);font-size:12px;font-weight:600}
        .bfu-step-dot{width:20px;height:20px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-weight:700;background:var(--bfu-bd);color:var(--bfu-tx)}
        .bfu-step.active{background:var(--bfu-soft);color:var(--bfu-p)}
        .bfu-step.active .bfu-step-dot{background:var(--bfu-p);color:#fff}
        .bfu-step.done{color:var(--bfu-ok);background:var(--bfu-ok-bg)}
        .bfu-step.done .bfu-step-dot{background:var(--bfu-ok);color:#fff}
        .bfu-input-icon{right:15px;top:50%;transform:translateY(-50%);font-size:19px;text-decoration:none!important}

        .bfu-empty{text-align:center;padding:52px 20px}
        .bfu-empty-icon{width:74px;height:74px;border-radius:50%;display:inline-grid;place-items:center;font-size:36px;color:var(--bfu-p);background:var(--bfu-soft);margin-bottom:14px}
        .bfu-empty h5{margin-bottom:6px;color:var(--bfu-tx)}
        .bfu-empty p{color:var(--bfu-mu);margin-bottom:14px}

        .bfu-busstrip{display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding:12px 16px;margin-bottom:12px;background:var(--bfu-bg);border:1px solid var(--bfu-bd);border-radius:14px}
        .bfu-busstrip-icon{width:40px;height:40px;border-radius:11px;display:grid;place-items:center;font-size:21px;background:var(--bfu-soft);color:var(--bfu-p)}
        .bfu-busstrip-main{display:flex;flex-direction:column;gap:2px}.bfu-busstrip-main strong{font-size:15px;color:var(--bfu-tx)}
        .bfu-busstrip-meta{margin-left:auto;display:flex;gap:16px;flex-wrap:wrap;font-size:12.5px;color:var(--bfu-mu)}.bfu-busstrip-meta i{margin-right:5px;color:var(--bfu-p)}

        .bfu-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:10px}
        .bfu-stat{display:flex;align-items:center;gap:11px;padding:12px 14px;background:var(--bfu-bg);border:1px solid var(--bfu-bd);border-radius:12px}
        .bfu-stat small{display:block;font-size:11px;margin-bottom:2px}.bfu-stat strong{font-size:15px;color:var(--bfu-tx)}
        .bfu-stat-icon{width:36px;height:36px;border-radius:10px;display:grid;place-items:center;font-size:19px;flex-shrink:0}
        .bfu-tone-neutral .bfu-stat-icon{background:var(--bfu-sub);color:var(--bfu-mu)}
        .bfu-tone-warn .bfu-stat-icon{background:var(--bfu-warn-bg);color:var(--bfu-warn)}
        .bfu-tone-primary .bfu-stat-icon{background:var(--bfu-soft);color:var(--bfu-p)}
        .bfu-tone-ok .bfu-stat-icon{background:var(--bfu-ok-bg);color:var(--bfu-ok)}
        .bfu-tone-danger .bfu-stat-icon{background:var(--bfu-danger-bg);color:var(--bfu-danger)}
        .bfu-tone-danger strong{color:var(--bfu-danger)}.bfu-tone-ok strong{color:var(--bfu-ok)}

        .bfu-collect{margin-bottom:14px}
        .bfu-collect-top{display:flex;justify-content:space-between;font-size:12px;color:var(--bfu-mu);margin-bottom:5px}.bfu-collect-top strong{color:var(--bfu-ok)}
        .bfu-bar{height:8px;border-radius:20px;background:var(--bfu-sub);overflow:hidden}
        .bfu-bar span{display:block;height:100%;border-radius:20px;background:linear-gradient(90deg,#43b36a,#2e9e4f);transition:width .3s}
        .bfu-bar-thin{height:3px;border-radius:0}.bfu-bar-thin span{border-radius:0}

        .bfu-toolbar{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:12px}
        .bfu-chips{display:flex;gap:6px;flex-wrap:wrap}
        .bfu-chipbtn{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;border-radius:30px;border:1px solid var(--bfu-bd);background:var(--bfu-bg);color:var(--bfu-mu);font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
        .bfu-chipbtn:hover{border-color:var(--bfu-p);color:var(--bfu-p)}
        .bfu-chipbtn.active{background:var(--bfu-p);border-color:var(--bfu-p);color:#fff}
        .bfu-chipcount{min-width:20px;padding:1px 6px;border-radius:20px;font-size:11px;text-align:center;background:var(--bfu-sub);color:inherit}
        .bfu-chipbtn.active .bfu-chipcount{background:rgba(255,255,255,.25)}
        .bfu-toolbar-actions{display:flex;gap:6px;flex-wrap:wrap}

        .bfu-list{display:flex;flex-direction:column;gap:10px}
        .bfu-card{background:var(--bfu-bg);border:1px solid var(--bfu-bd);border-radius:13px;overflow:hidden;transition:border-color .15s,box-shadow .15s}
        .bfu-card:hover{box-shadow:0 4px 16px rgba(28,39,69,.07)}
        .bfu-card.is-dirty{border-color:var(--bfu-warn);box-shadow:0 0 0 1px var(--bfu-warn-bg)}
        .bfu-card-head{display:flex;align-items:center;gap:13px;padding:12px 14px;cursor:pointer;flex-wrap:wrap}
        .bfu-card-head:focus-visible{outline:2px solid var(--bfu-p);outline-offset:-2px}
        .bfu-chev{font-size:20px;color:var(--bfu-mu);transition:transform .15s}
        .bfu-card.is-open .bfu-chev{transform:rotate(90deg)}
        .bfu-avatar{width:40px;height:40px;border-radius:11px;display:grid;place-items:center;font-size:14px;font-weight:700;flex-shrink:0}
        .bfu-av-0{background:rgba(105,108,255,.15);color:#5a5de0}.bfu-av-1{background:rgba(46,158,79,.15);color:#2e9e4f}.bfu-av-2{background:rgba(217,138,0,.16);color:#b87400}.bfu-av-3{background:rgba(229,72,77,.14);color:#d13a3f}.bfu-av-4{background:rgba(59,130,246,.15);color:#2f6fd6}.bfu-av-5{background:rgba(168,85,247,.15);color:#8e42d6}
        .bfu-who{display:flex;flex-direction:column;gap:2px;min-width:170px;flex:1.3}
        .bfu-who strong{font-size:14px;color:var(--bfu-tx)}.bfu-who small{font-size:11.5px}
        .bfu-sn{color:var(--bfu-mu);font-weight:500;font-size:12px}
        .bfu-stop{display:flex;flex-direction:column;gap:2px;min-width:150px;flex:1}
        .bfu-stop span{font-size:12.5px;font-weight:600;color:var(--bfu-tx)}.bfu-stop i{color:var(--bfu-p);margin-right:4px}.bfu-stop small{font-size:11.5px}
        .bfu-mini{display:flex;gap:18px;flex:1.2;min-width:230px}
        .bfu-mini div{display:flex;flex-direction:column;gap:2px}.bfu-mini small{font-size:10.5px;text-transform:uppercase;letter-spacing:.3px}.bfu-mini strong{font-size:13.5px;color:var(--bfu-tx)}
        .bfu-head-actions{display:flex;align-items:center;gap:10px;margin-left:auto}
        .bfu-unsaved{display:inline-flex;align-items:center;gap:4px;padding:4px 9px;border-radius:20px;font-size:11px;font-weight:700;background:var(--bfu-warn-bg);color:var(--bfu-warn)}

        .bfu-msg{display:flex;align-items:center;gap:7px;padding:7px 16px;font-size:12px;font-weight:600}
        .bfu-msg.ok{background:var(--bfu-ok-bg);color:var(--bfu-ok)}.bfu-msg.err{background:var(--bfu-danger-bg);color:var(--bfu-danger)}

        .bfu-body{padding:14px;border-top:1px solid var(--bfu-bd);background:var(--bfu-sub)}
        .bfu-tiles{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:10px}
        .bfu-tile{background:var(--bfu-bg);border:1px solid var(--bfu-bd);border-radius:11px;padding:11px}
        .bfu-tile.is-changed{border-color:var(--bfu-warn);background:linear-gradient(0deg,var(--bfu-warn-bg),var(--bfu-warn-bg)),var(--bfu-bg)}
        .bfu-tile-empty{display:flex;flex-direction:column;justify-content:center;align-items:center;gap:3px;border-style:dashed;background:transparent;color:var(--bfu-mu);min-height:100px}
        .bfu-tile-empty strong{font-size:13px}
        .bfu-tile-top{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:9px}
        .bfu-tile-top strong{display:block;font-size:13.5px;color:var(--bfu-tx)}.bfu-tile-top small{font-size:11px}
        .bfu-label{display:block;font-size:10.5px;text-transform:uppercase;letter-spacing:.3px;color:var(--bfu-mu);margin-bottom:4px;font-weight:600}
        .bfu-tile-meta{display:flex;justify-content:space-between;align-items:center;gap:6px;margin-top:7px;font-size:11.5px;color:var(--bfu-mu)}
        .bfu-tile-meta b{font-weight:700}
        .bfu-off{padding:1px 7px;border-radius:20px;font-weight:700;font-size:10.5px;background:var(--bfu-warn-bg);color:var(--bfu-warn)}
        .bfu-hint{margin-top:8px;padding:5px 8px;border-radius:7px;font-size:10.5px;background:var(--bfu-sub);color:var(--bfu-mu)}.bfu-hint i{margin-right:4px}

        .bfu-chip{padding:3px 9px;border-radius:20px;font-size:10.5px;font-weight:700;white-space:nowrap}
        .bfu-chip-paid{background:var(--bfu-ok-bg);color:var(--bfu-ok)}
        .bfu-chip-partial{background:var(--bfu-warn-bg);color:var(--bfu-warn)}
        .bfu-chip-due{background:var(--bfu-danger-bg);color:var(--bfu-danger)}
        .bfu-chip-waived{background:var(--bfu-info-bg);color:var(--bfu-info)}
        .bfu-chip-new{background:var(--bfu-p);color:#fff}

        .bfu-foot{display:flex;gap:16px;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;margin-top:14px;padding-top:14px;border-top:1px dashed var(--bfu-bd)}
        .bfu-remark{flex:1;min-width:240px;max-width:420px}
        .bfu-totals{display:flex;gap:20px;flex-wrap:wrap}
        .bfu-totals div{display:flex;flex-direction:column;gap:2px}.bfu-totals small{font-size:10.5px;text-transform:uppercase;letter-spacing:.3px}.bfu-totals strong{font-size:14px;color:var(--bfu-tx)}

        .bfu-floatbar{position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:1045;display:flex;align-items:center;gap:20px;padding:11px 14px 11px 18px;border-radius:14px;background:#2b2c40;color:#fff;box-shadow:0 12px 36px rgba(20,30,65,.35);max-width:calc(100vw - 24px);flex-wrap:wrap;justify-content:center}
        .bfu-floatbar i{margin-right:6px;color:#ffc861}

        .bfu-preview{border:1px dashed var(--bfu-bd);border-radius:11px;padding:12px;background:var(--bfu-sub)}
        .bfu-preview-title{font-size:12px;font-weight:700;color:var(--bfu-p);margin-bottom:8px}
        .bfu-preview-months{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px}
        .bfu-preview-months span{padding:3px 9px;border-radius:20px;font-size:11.5px;font-weight:600;background:var(--bfu-soft);color:var(--bfu-p)}
        .bfu-preview-text{font-size:12.5px;color:var(--bfu-tx)}

        @media(max-width:1200px){.bfu-stats{grid-template-columns:repeat(3,minmax(0,1fr))}.bfu-mini{min-width:100%;order:5}.bfu-head-actions{margin-left:0}}
        @media(max-width:767px){.bfu-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.bfu-card-head{gap:10px}.bfu-who,.bfu-stop{min-width:calc(100% - 100px)}.bfu-step-label{display:none}.bfu-step.active .bfu-step-label{display:inline}.bfu-busstrip-meta{margin-left:0}.bfu-floatbar{width:calc(100vw - 24px);justify-content:space-between}}
      `}</style>
    </>
  );
}
